// -------------------------------
// SKILL INTERFACES
// -------------------------------
export interface PendingSkill {
    name?: string;
    description?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteSkill {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE SKILL — START
// -------------------------------
export const startCreateSkillFlow = (
    setPendingSkill: (data: PendingSkill | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingSkill({ name: nameFromIntent });

        pushAIChat(`Great! Can you provide a description for "${nameFromIntent}"?`);
        speak(`Please provide a description for ${nameFromIntent}.`);
        return;
    }

    setPendingSkill({});
    pushAIChat("Sure! What should the skill be called?");
    speak("What should the skill be called?");
};

// -------------------------------
// CREATE SKILL — PROCESS
// -------------------------------
export const processPendingSkill = (
    text: string,
    pending: PendingSkill,
    setPendingSkill: (data: PendingSkill | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Name
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid skill name.");
            speak("Please provide a valid skill name.");
            return;
        }

        setPendingSkill({ ...pending, name: cleanName });

        pushAIChat(`Please provide a description for "${cleanName}".`);
        speak(`Please provide a description for ${cleanName}.`);
        return;
    }

    // STEP 2 — Description
    if (!pending.description) {
        setPendingSkill({ ...pending, description: text });

        pushAIChat("Should this skill be active or inactive?");
        speak("Should this skill be active or inactive?");
        return;
    }

    // STEP 3 — Status
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        setPendingSkill({ ...pending, status });

        // FINAL — CREATE SKILL
        pushAIChat(`Perfect! Creating skill "${pending.name}"...`);
        speak(`Creating skill ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-skill", {
                detail: {
                    name: pending.name,
                    description: pending.description,
                    status
                }
            })
        );

        setPendingSkill(null);
        return;
    }
};

// -------------------------------
// DELETE SKILL — START
// -------------------------------
export const startDeleteSkillFlow = (
    setPendingDelete: (data: PendingDeleteSkill | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting skill "${nameFromIntent}"...`);
        speak(`Deleting skill ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-skill", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which skill would you like to delete?");
    speak("Which skill would you like to delete?");
};

// -------------------------------
// DELETE SKILL — PROCESS
// -------------------------------
export const processPendingDeleteSkill = (
    text: string,
    pending: PendingDeleteSkill,
    setPendingDelete: (data: PendingDeleteSkill | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid skill name.");
            speak("Please provide a valid skill name.");
            return;
        }

        pushAIChat(`Deleting skill "${cleanName}"...`);
        speak(`Deleting skill ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-skill", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
    }
};
